import { FC, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { StepRenderProps } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import { OnboardingMeetingsPermissionsSetup } from "@/components/onboarding/components/welcome-wizard/steps/system-permissions-meetings/components";
import { useOnboardingSystemPermissionsHandlers } from "@/components/onboarding/components/welcome-wizard/shared";
import { useAppStore } from "@/store";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

export const SystemPermissionsMeetingPermissionsStep: FC<StepRenderProps> = observer(
  ({ onNavToStep, onClose }) => {
    const { store } = useAppStore();
    const ignorePermissions = !store.interface.isMacOs || store.electron.isIntelMacDesktop;

    const hasVoiceSystemPermissionsStepNext =
      store.platformInfo.perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions;

    const handleNext = useCallback(
      ({ autoMeetingDetectionEnabled }: { autoMeetingDetectionEnabled: boolean }) => {
        trackEvent(TrackedEvent.MeetingNotificationsSetupSubmit, {
          enabled: autoMeetingDetectionEnabled,
        });

        /**
         * After this, we are done with the system-permissions-meeting flow.
         */
        onClose?.();

        if (hasVoiceSystemPermissionsStepNext) {
          onNavToStep?.("system-permissions-voice-permissions");
        }
      },
      [hasVoiceSystemPermissionsStepNext, onClose, onNavToStep]
    );

    const {
      resolveConfigurationStatus,
      handleEnablePermission,
      handleOpenPermissionPane,
      /**
       * Restart shouldn't be required for any onboarding-related meeting permissions.
       */
      restartRequired: _restartRequired,
    } = useOnboardingSystemPermissionsHandlers();

    useEffectOnMount(() => {
      trackEvent(TrackedEvent.MeetingNotificationsSetupView);
    });

    return (
      <OnboardingMeetingsPermissionsSetup
        onNext={handleNext}
        ignorePermissions={ignorePermissions}
        hasNextStep={hasVoiceSystemPermissionsStepNext}
        resolveConfigurationStatus={resolveConfigurationStatus}
        handleEnablePermission={handleEnablePermission}
        handleOpenPermissionPane={handleOpenPermissionPane}
      />
    );
  }
);
